import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Mic, MicOff, Volume2, VolumeX, Sparkles, MapPin, Menu as MenuIcon,
         ShoppingBag, Plus, ChevronRight, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { rupee } from "../lib/format";
import { useCart } from "../context/CartContext";
import { Logo, PoweredBy } from "../components/Brand";

const GREETING = "Hi, I'm your Food Buddy! 👋 Tell me what you're craving — like \"a chicken biryani, two gulab jamun and a cappuccino\" — and I'll pull it together from across the food court and get it ordered. What can I get you?";

export default function FoodBuddy() {
  const navigate = useNavigate();
  const { table, addItem, totals, grouped, clear } = useCart();
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [listening, setListening] = useState(false);
  const [speak, setSpeak] = useState(false);
  const [sessionId] = useState(() => `buddy_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => { if (!table) navigate("/"); }, [table]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, loading]);

  const say = (text) => {
    if (!speak || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((p) => [...p, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/public/agent/chat", {
        foodcourt_id: table.foodcourt_id, table_code: table.table_code,
        message: msg, session_id: sessionId, history,
      });
      if (data.clear_cart) clear();
      let added = 0;
      (data.add_lines || []).forEach((l) => {
        addItem({ id: l.item_id, restaurant_id: l.restaurant_id, restaurant_name: l.restaurant_name,
          group_id: l.group_id, group_name: l.group_name, name: l.name, price: l.price,
          image: l.image, food_type: l.food_type, prep_time: l.prep_time }, l.variant, [], l.qty);
        added += l.qty;
      });
      setSuggestions(data.suggestions || []);
      setMessages((p) => [...p, { role: "assistant", content: data.reply, added, lines: data.add_lines || [] }]);
      say(data.reply);
      if (data.ready_to_pay && totals.count + added > 0) {
        setTimeout(() => navigate("/checkout"), 700);
      }
    } catch (e) {
      toast.error(apiErr(e));
      setMessages((p) => [...p, { role: "assistant", content: "Sorry, I glitched — try again?" }]);
    } finally { setLoading(false); }
  };

  const addSuggestion = (s) => {
    const it = s.item;
    addItem({ id: it.id, restaurant_id: it.restaurant_id, restaurant_name: it.restaurant_name,
      group_id: it.group_id, group_name: it.group_name, name: it.name, price: it.price,
      image: it.image, food_type: it.food_type, prep_time: it.prep_time },
      it.variants?.[0] || null, [], 1);
    toast.success(`Added ${it.name}`);
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input isn't supported on this browser"); return; }
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    const r = new SR();
    r.lang = "en-IN"; r.interimResults = false; r.maxAlternatives = 1;
    r.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); setListening(false); send(t); };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r; r.start(); setListening(true);
  };

  return (
    <div className="max-w-md mx-auto h-screen bg-[#08183A] flex flex-col relative">
      {/* header */}
      <div className="glass px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-xl gold-gradient flex items-center justify-center shrink-0 pulse-gold">
            <Bot className="text-slate-950" size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-sm text-slate-50">Food Buddy</div>
            <div className="flex items-center gap-1 text-[10px] text-amber-400"><MapPin size={10} /><span className="truncate">{table?.foodcourt_name} · Table {table?.table_code}</span></div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setSpeak((s) => !s)} data-testid="tts-toggle"
            className={`h-8 w-8 rounded-full flex items-center justify-center ${speak ? "gold-gradient text-slate-950" : "card-surface-2 text-slate-300"}`}>
            {speak ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          <button onClick={() => navigate("/menu")} data-testid="open-menu-btn"
            className="card-surface-2 rounded-full px-3 h-8 flex items-center gap-1.5 text-xs text-slate-200">
            <MenuIcon size={14} /> Menu
          </button>
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm animate-fadeup ${m.role === "user"
              ? "gold-gradient text-slate-950 font-medium rounded-br-sm"
              : "card-surface text-slate-100 rounded-bl-sm"}`} data-testid={`msg-${m.role}`}>
              {m.content}
              {m.added > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-950/20 space-y-0.5">
                  {m.lines.map((l, j) => (
                    <div key={j} className="text-[11px] flex items-center gap-1 opacity-90">
                      <Plus size={11} /> {l.qty}× {l.name}{l.variant ? ` (${l.variant.name})` : ""} — {l.restaurant_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start"><div className="card-surface rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
            <span className="h-2 w-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="h-2 w-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="h-2 w-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div></div>
        )}

        {suggestions.length > 0 && !loading && (
          <div className="pt-1">
            <div className="flex items-center gap-1 text-[11px] text-amber-400 mb-1.5"><Sparkles size={12} /> You might also like</div>
            <div className="flex flex-wrap gap-2" data-testid="buddy-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => addSuggestion(s)} data-testid={`suggestion-${s.item.id}`}
                  className="card-surface rounded-full pl-1 pr-3 py-1 flex items-center gap-2 text-xs hover:border-amber-500 transition-colors">
                  <img src={s.item.image} alt="" className="h-6 w-6 rounded-full object-cover" />
                  <span className="text-slate-100">{s.item.name}</span>
                  <span className="text-amber-400 font-semibold">{rupee(s.item.price)}</span>
                  <Plus size={13} className="text-amber-400" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* cart summary */}
      {totals.count > 0 && (
        <button onClick={() => navigate("/checkout")} data-testid="buddy-review-pay"
          className="mx-4 mb-2 gold-gradient text-slate-950 rounded-xl px-4 py-2.5 flex items-center justify-between shrink-0">
          <span className="flex items-center gap-2 text-sm font-bold"><ShoppingBag size={16} /> {totals.count} items · {totals.restaurants} rest.</span>
          <span className="flex items-center gap-1 text-sm font-extrabold">Review & Pay {rupee(totals.payable)} <ChevronRight size={16} /></span>
        </button>
      )}

      {/* input */}
      <div className="glass px-3 py-3 shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={toggleMic} data-testid="mic-btn"
            className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${listening ? "bg-red-500 text-white pulse-gold" : "card-surface-2 text-amber-400"}`}>
            {listening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={listening ? "Listening…" : "Tell Food Buddy what you want…"}
            className="flex-1 bg-[#16336B] rounded-full px-4 py-3 text-sm text-slate-50 placeholder-slate-400 border hairline focus:border-amber-500 outline-none"
            data-testid="buddy-input" />
          <button onClick={() => send()} disabled={loading || !input.trim()} data-testid="buddy-send"
            className="h-11 w-11 rounded-full gold-gradient flex items-center justify-center shrink-0 disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin text-slate-950" size={18} /> : <Send className="text-slate-950" size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
